import React from "react";
import { makeStyles } from "@material-ui/core";
import { IconButton, Divider, Grid, List, ListItem } from "@material-ui/core";
import { ReactComponent as FacebookIcon } from "../../images/icons/facebook.svg";
import { ReactComponent as InstagramIcon } from "../../images/icons/instagram.svg";

import { serviceLink, privacyLink } from "../../utils/const";

import classNames from "classnames";

// context
import { useUserState } from "../../context/UserContext";

// styles
const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(10, "6.5%"),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(8, "6.5%"),
    },
    paddingTop: theme.spacing(4),
    background: "#424242",
  },
  footerContainer: {
    maxWidth: 1100,
    width: "100%",
    margin: "0 auto",
    padding: theme.spacing(0, 2),
  },
  bottomContainer: {
    maxWidth: 1300,
    width: "100%",
    margin: "0 auto",
    padding: theme.spacing(5, 2),
  },
  logoContainerItem: {
    paddingTop: 0,
    [theme.breakpoints.down("sm")]: {
      paddingTop: theme.spacing(5),
      paddingLeft: theme.spacing(0),
    },
  },
  logoContainer: {
    width: 250,
    height: 32,
  },
  logoImage: {
    width: "100%",
    height: "100%",
  },
  groupTitle: {
    textTransform: "uppercase",
    color: theme.palette.primary.dark,
    marginBottomContainer: theme.spacing(1),
  },
  socialIcon: {
    padding: 0,
    marginRight: theme.spacing(1),
    color: "rgba(255,255,255,.6)",
    "&:hover": {
      background: "transparent",
    },
    "&:last-child": {
      marginRight: 0,
    },
  },
  icon: {
    fontSize: 30,
  },
  menuListContainer: {
    padding: "0 !important",
  },
  menu: {
    display: "flex",
  },
  menuItem: {
    margin: theme.spacing(2),
    "&:last-child": {
      marginBottomContainer: 0,
    },
  },
  listItem: {
    marginRight: theme.spacing(10),
    [theme.breakpoints.down("md")]: {
      marginRight: theme.spacing(2),
    },
  },
  menuGroupItem: {
    paddingTop: 0,
    paddingBottomContainer: theme.spacing(1 / 2),
    "&:last-child": {
      paddingBottomContainer: 0,
    },
  },
  menuGroupTitle: {
    ...theme.typography.caption,
    textTransform: "uppercase",
    color: "#afafaf",
    fontWeight: theme.typography.fontWeightBold,
  },
  submenuItem: {
    ...theme.typography.caption,
    fontWeight: theme.typography.fontWeightRegular,
    textDecoration: "none",
  },
  divider: {
    marginTop: theme.spacing(2),
    width: "100%",
    backgroundColor: theme.palette.common.grey[400],
  },
  navLink: {
    color: "white",
  },
  hint: {
    fontSize: 10,
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.common.grey[400],
    marginTop: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      textAlign: "center",
    },
  },
  text1: {
    ...theme.typography.h4,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1,
    letterSpacing: 5,
    color: "white",
  },
  text2: {
    ...theme.typography.caption,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 2,
    color: "white",
    marginTop: theme.spacing(1),
  },
  text3: {
    fontSize: 10,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.6,
    color: "white",
    marginTop: theme.spacing(1.5),
  },
}));

export default function Footer () {
  const classes = useStyles();
  const { platform } = useUserState();
  const { intro_url = "" } = platform;

  const footer = {
    information: {
      groupTitle: "平台資訊",
      pages: [
        {
          title: "關於我們",
          href: intro_url,
        },
        {
          title: "聯絡我們",
          href: "/contact-us",
        },
      ],
    },
    seed: {
      groupTitle: "視界播種",
      pages: [
        {
          title: "關於計畫",
          href: "",
        },
        {
          title: "申請種子教師",
          href: "",
        },
        {
          title: "CSR合作",
          href: "",
        },
      ],
    },
    policy: {
      groupTitle: "用戶條款",
      pages: [
        {
          title: "用戶服務條款",
          href: serviceLink,
        },
        {
          title: "服務隱私權政策",
          href: privacyLink,
        },
      ],
    },
  };

  footer.information.pages =
    platform.name === "crt"
      ? [
          {
            title: "關於我們",
            href: intro_url,
          },
          {
            title: "聯絡我們",
            href: "/contact-us",
          },
        ]
      : [
          {
            title: "關於我們",
            href: intro_url,
          },
          {
            title: "聯絡我們",
            href: "/contact-us",
          },
        ];

  const { information, seed, policy } = footer;

  const MenuGroup = props => {
    const { item } = props;
    return (
      <List disablePadding className={classes.menuItem}>
        <ListItem disableGutters className={classes.menuGroupItem}>
          <div className={classes.menuGroupTitle}>{item.groupTitle}</div>
        </ListItem>
        {item.pages.map((page, i) => (
          <ListItem disableGutters key={i} className={classes.menuGroupItem}>
            <a
              href={page.href}
              className={classNames(classes.navLink, classes.submenuItem)}
            >
              {page.title}
            </a>
          </ListItem>
        ))}
      </List>
    );
  };

  const InformationPages = () => (
    <div className={classes.menu}>
      <div>
        <MenuGroup item={information} />
      </div>
    </div>
  );

  const SeedPages = () => (
    <div className={classes.menu}>
      <div>
        <MenuGroup item={seed} />
      </div>
    </div>
  );

  const PolicyPages = () => (
    <div className={classes.menu}>
      <div>
        <MenuGroup item={policy} />
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.footerContainer}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} className={classes.menuListContainer}>
            <Grid container spacing={5}>
              <Grid item className={classes.listItem}>
                <InformationPages />
              </Grid>
              <Grid item className={classes.listItem}>
                <SeedPages />
              </Grid>
              <Grid item className={classes.listItem}>
                <PolicyPages />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <List disablePadding>
              <ListItem disableGutters className={classes.logoContainerItem}>
                <div className={classes.logoContainer}>
                  <Grid container>
                    <Grid item xs={8}>
                      <div>
                        <div className={classes.text1}>視界影視文化</div>
                        <div className={classes.text2}>
                          教室電影院公播大平台
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        className={classes.socialIcon}
                        onClick={() =>
                          window.open(
                            "https://www.facebook.com/crt.avjet",
                            "_blank",
                          )
                        }
                      >
                        <FacebookIcon className={classes.icon} />
                      </IconButton>
                      <IconButton
                        className={classes.socialIcon}
                        onClick={() =>
                          window.open(
                            "https://www.instagram.com/classroomtheater",
                            "_blank",
                          )
                        }
                      >
                        <InstagramIcon className={classes.icon} />
                      </IconButton>
                    </Grid>
                  </Grid>

                  <div className={classes.text3}>
                    © 2020 VISION MEDIA All Rights Reserved.
                  </div>
                </div>
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </div>
      <div className={classes.bottomContainer}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Divider classes={{ root: classes.divider }} />
            <div className={classes.hint}>
              建議使用 Chrome、Firefox、Safari
              瀏覽器，以獲得最佳瀏覽體驗。由於Html5語法支援度問題，無法使用 IE11
              版本以下瀏覽器瀏覽。
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
